import React, {useState, useEffect, useRef} from 'react';
import {Modal, ModalHeader, ModalBody} from "../Modal";
import CustomButton from "../CustomButton";
import Api from "../../services/Api";

interface Props {
  picture: string | undefined;
  close: Function
  lang: string
  who: string
}

const EditPicture: React.FC<Props> = ({ picture, close, lang, who }) => {
  const api = Api.getInstance();
  const rawImage = useRef<HTMLImageElement|null>(null);
  const [renderedPic, setRenderedPic] = useState<string>('');
  const [pictureChanged, setPictureChanged] = useState<boolean>(false);
  const [scale, setScale] = useState<number>(100)
  const [offsetX, setOffsetX] = useState<number>(0)
  const [offsetY, setOffsetY] = useState<number>(0)

  useEffect(() => {
    if (picture) {
      api.getRaw(who + '/picture').then(response => {
        const reader = response.body.getReader();
          return new ReadableStream({
            start(controller) {
              return pump();
              function pump() {
                return reader.read().then(({ done, value }) => {
                  // When no more data needs to be consumed, close the stream
                  if (done) {
                    controller.close();
                    return;
                  }
                  // Enqueue the next data chunk into our target stream
                  controller.enqueue(value);
                  return pump();
                });
              }
            },
          });
        })
        // Create a new response out of the stream
        .then((stream) => new Response(stream))
        // Create an object URL for the response
        .then((response) => response.blob())
        .then((blob) => URL.createObjectURL(blob))
        // Update image
        .then((url) => createRawImage(url))
        .catch((err) => console.error(err));
    }
  }, [])

  useEffect(() => {
    if (rawImage.current) {
      setPictureChanged(true)
      renderFromRaw()
    }
  }, [scale, offsetX, offsetY])

  const createRawImage = (src: string) => {
    let img = document.createElement("img")
    img.classList.add("hidden")
    img.src = src
    rawImage.current = img
    img.addEventListener("load", (e) => {
      renderFromRaw()
    })
  }

  const renderFromRaw = ()=> {
    if (rawImage.current === null) {
      console.log('rawImage not loaded yet')
      return
    }
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    const nWidth = rawImage.current.naturalWidth;
    const nHeight = rawImage.current.naturalHeight;
    const iWidth = rawImage.current.width;
    const iHeight = rawImage.current.height;
    const cx = iWidth / 2;
    const cy = iHeight / 2;
    const radius = (100 / scale) * Math.min(iWidth, iHeight) / 2
    const sWidth = 2 * radius * nWidth / iWidth;
    const sHeight = 2 * radius * nHeight / iHeight;
    let sx = (cx - radius) * nWidth / iWidth;
    let sy = (cy - radius) * nHeight / iHeight;
    if (offsetX != 0) {
      sx = sx + offsetX * sx / 50
    }
    if (offsetY != 0) {
      sy = sy - offsetY * sy / 50
    }

    if (ctx !== null) {
      ctx.drawImage(rawImage.current, sx, sy, sWidth, sHeight, 0, 0, 300, 300);
      setRenderedPic(canvas.toDataURL('image/jpeg'))
    }
  }

  const saveImage = () => {
    const data = {
      file_data: renderedPic
    }
    api.post(who + '/upload/picture', data).then(result => {
      close(result.json.fileName)
    })
  }

  const uploadImage = (evt: any) => {
    if (evt === null) {
      alert('Empty event');
      return;
    }

    const picFile = evt.target.files[0];
    if (!picFile) {
      console.log('No pic file')
      return
    }

    if (!picFile.type.match('image.*')) {
      alert('This is not an image');
      return;
    }

    const reader = new FileReader();

    reader.onload = ((theFile) => {
      return (e: any) => {
        setPictureChanged(true)
        createRawImage(e.target.result)
      };
    })(picFile);
    reader.readAsDataURL(picFile);
  }

  let moveDisabled = scale === 100

  return (
    <>
      <Modal isOpen={true} onClose={() => close(pictureChanged)}>
        <ModalHeader onClose={() => close(pictureChanged)}>
          {lang === 'sv' ? 'Profilbild' : 'Profile picture'}
        </ModalHeader>
        <ModalBody>
          <>
            {
              renderedPic.length > 0 ? (
                <div className="text-center">

                  <div className="flex justify-center mt-4 overflow-hidden">
                    <img alt="picture" style={{borderRadius: "50%"}} src={renderedPic}/>

                  </div>

                  <div className="mt-6 mx-auto" style={{width: 300}}>
                    <div>{lang === 'sv' ? 'Flytta ner/upp' : 'Move down/up'}</div>
                    <input className="mt-2 w-full" type="range" disabled={moveDisabled} min="-50" max="50" value={offsetY}
                           onChange={(e) => setOffsetY(parseInt(e.target.value))}/>
                    <div className="mt-4">{lang === 'sv' ? 'Flytta vänster/höger' : 'Move left/right'}</div>
                    <input className="mt-2 w-full" disabled={moveDisabled} type="range" min="-50" max="50" value={offsetX}
                           onChange={(e) => setOffsetX(parseInt(e.target.value))}/>
                    <div className="mt-4">{lang === 'sv' ? 'Zooma in/ut' : 'Zoom in/out'}</div>
                    <input className="mt-2 w-full" type="range" min="100" max="600" value={scale}
                           onChange={(e) => setScale(parseInt(e.target.value))}
                           id="myRange"/>
                  </div>

                </div>
              ) : (
                <div style={{width: "300px", height: "300px"}} className="p-4">
                  &nbsp;
                </div>
              )
            }

            <div className="cursor-pointer mt-4 flex justify-center">
              <input id="profileFile" type="file" className="hidden" onChange={(e: any) => uploadImage(e)}/>
              <label htmlFor="profileFile" className="underline cursor-pointer">
                {
                  lang === 'sv' ?
                    pictureChanged ? 'Klicka här för att välja ny bild' : 'Klicka här för att välja bild' :
                    pictureChanged ? 'Click here to select new picture' : 'Click here to select picture'
                }
              </label>
            </div>

            <div className={pictureChanged ? 'visible' : 'invisible'}>
              <CustomButton className="mt-4 w-full" onClick={() => saveImage()}>Spara</CustomButton>
            </div>
          </>
        </ModalBody>
      </Modal>
    </>
  );
};

export default EditPicture;
